import * as React from "react"
import { ConsumerDetailsType, PaginationType } from "@/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, redirect } from "@tanstack/react-router"
import type { DateRange } from "react-day-picker"

import { ConsumersOverviewType, WeeklyPaymentsChartType } from "@/types/"
import {
  ConsumerStatsOverviewType,
  meterDailyAnalyticsCountType,
} from "@/types/meter"
import { consumerChartConfig } from "@/config/charts"
import { canViewThisPage } from "@/lib/api/auth"
import {
  getBillingOverview,
  getConsumerDetails,
  getWeeklyPayments,
  sendEmailReminder,
} from "@/lib/api/billing"
import {
  getConsumersAnalyticsPieChart,
  getConsumersDailyAnalyticsCount,
  getConsumersOverview,
  getConsumersStatusOverview,
} from "@/lib/api/consumers"
import { getMeterDailyAnalyticsCount } from "@/lib/api/meter"
import { getStatusStyle } from "@/lib/statusStyles"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { BarChartStacked } from "@/components/charts/barchart-stacked"
import { PieChartComponent } from "@/components/charts/piechart"
import { DataTable } from "@/components/data-table"
import { DateRangePicker } from "@/components/date-range-picker"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"
import SelectTimeRange, {
  timeRangeOptions,
} from "@/components/select-time-range"
import { consumerDetailsColumns } from "@/components/table/columns/consumer-details-columns"
import { ExportOperationModal } from "@/components/table/operations/export-operations"

export const Route = createFileRoute("/_home/manage-consumers/")({
  beforeLoad: () => {
    if (canViewThisPage("himo_billing.view_consumers")) {
      return
    }
    return redirect({ to: "/access-denied" })
  },
  component: ManageUsersPage,
})

function ManageUsersPage() {
  const [showExportModal, setShowExportModal] = React.useState<boolean>(false)
  const [timeRange, setTimeRange] = React.useState(timeRangeOptions()[0])
  const [date, setDate] = React.useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
    to: new Date(), // Today
  })
  const [consumerDailyAnalyticsCountDate, setConsumerDailyAnalyticsCountDate] =
    React.useState<DateRange>({
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    })

  const [consumerPieAnalyticsDate, setConsumerPieAnalyticsDate] =
    React.useState<DateRange>({
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    })

  const {
    data: consumerDailyAnalyticsCount,
    isLoading: consumerDailyAnalyticsCountLoading,
  } = useQuery({
    queryKey: [
      "consumerDailyAnalyticsCount",
      consumerDailyAnalyticsCountDate.from?.toISOString(),
      consumerDailyAnalyticsCountDate.to?.toISOString(),
    ],
    queryFn: async () => {
      const from = consumerDailyAnalyticsCountDate.from ?? new Date()
      const to = consumerDailyAnalyticsCountDate.to ?? new Date()
      return await getConsumersDailyAnalyticsCount(
        from.toISOString(),
        to.toISOString(),
      )
    },
  })

  const [defaultPage, setDefaultPage] = React.useState<number>(10)
  const [page, setPage] = React.useState<number>(1)
  const [search, setSearch] = React.useState<string>("")
  const [zones, setZones] = React.useState<number[]>([])
  const [books, setBooks] = React.useState<number[]>([])
  const [nextPageUrl, setNextPageUrl] = React.useState<string | null>(null)
  const [prevPageUrl, setPrevPageUrl] = React.useState<string | null>(null)

  const {
    data: consumersAnalyticsPieChartData,
    isLoading: isConsumersAnalyticsPieChartDataLoading,
    isError: isConsumersAnalyticsPieChartDataError,
  } = useQuery({
    queryKey: [
      "consumersAnalyticsPieChartData",
      consumerPieAnalyticsDate.from?.toISOString(),
      consumerPieAnalyticsDate.to?.toISOString(),
    ],
    queryFn: async () => {
      const from = consumerPieAnalyticsDate.from ?? new Date()
      const to = consumerPieAnalyticsDate.to ?? new Date()

      return await getConsumersAnalyticsPieChart(
        from.toISOString(),
        to.toISOString(),
      )
    },
  })

  const {
    data: consumerDetails,
    isError,
    isLoading,
  } = useQuery<PaginationType<ConsumerDetailsType>>({
    queryKey: ["consumerDetails", defaultPage, page, search, zones, books],
    queryFn: () => getConsumerDetails(defaultPage, page, search, zones, books),
  })

  React.useEffect(() => {
    if (consumerDetails) {
      setNextPageUrl(consumerDetails.next)
      setPrevPageUrl(consumerDetails.previous)
    }
  }, [consumerDetails])

  //console.log(consumerDetails)

  /*const { data: consumersOverview, isLoading: isConsumersOverviewIsLoading } =
    useQuery<ConsumersOverviewType>({
      queryKey: ["consumersOverview", timeRange.value],
      queryFn: () => getConsumersOverview(Number(timeRange.value)),
    }) */

  const {
    data: consumersStatusOverview,
    isLoading: isConsumersStatusOverviewLoading,
  } = useQuery<ConsumerStatsOverviewType>({
    queryKey: ["consumersStatusOverview", timeRange.value],
    queryFn: () => getConsumersStatusOverview(Number(timeRange.value)),
  })

  const { data: weeklyPayments } = useQuery<WeeklyPaymentsChartType[]>({
    queryKey: ["weeklyPayments", date],
    queryFn: () => getWeeklyPayments(date),
  })

  const renderOverviewCard = (
    title: string,
    icon: keyof typeof Icons,
    data?: {
      total: number | string
      percentage: number | null
      status: string
    },
  ) => {
    const IconComponent = Icons?.[icon] ?? Icons?.loader

    const {
      color,
      bgColor,
      icon: StatusIcon,
    } = getStatusStyle(data?.status ?? "", data?.percentage ?? null)
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex gap-2 text-[13px] font-medium text-muted-foreground">
            {icon === "peso" ? (
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary shadow-sm">
                <IconComponent className="h-4 w-4 text-white" />
              </div>
            ) : (
              <IconComponent className="h-[18px] w-[18px] text-primary" />
            )}

            <p className="line-height-[14px] font-worksans font-bold tracking-wider">
              {title}
            </p>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-2xl font-bold">
          {isConsumersStatusOverviewLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : data?.total !== undefined ? (
            <div className="flex flex-row items-center gap-2">
              <h1 className="font-semibold">
                {title === "Total Payments" ? `PHP ${data.total}` : data.total}
              </h1>
              {data.status !== "New" && (
                <span
                  className={`rounded-sm px-1 text-[13px] font-normal ${color} ${bgColor}`}
                >
                  {data.status !== "Flat" && data.percentage !== null
                    ? `${data.percentage.toFixed(2)}%`
                    : ""}

                  {StatusIcon && (
                    <span
                      className={
                        data.status === "Flat"
                          ? "flex items-center"
                          : "inline-block px-1"
                      }
                    >
                      {StatusIcon}
                    </span>
                  )}
                </span>
              )}
            </div>
          ) : (
            "N/A"
          )}
        </CardContent>
      </Card>
    )
  }

  const items = Object.entries(consumersStatusOverview?.data || {})
  const itemsCount = items.length

  // Dynamically decide how many items per row
  let itemsPerRow =
    itemsCount <= 4 ? 4 : itemsCount <= 6 ? 6 : Math.ceil(itemsCount / 2)

  // Adjust itemsPerRow if it's 5 to be 6
  itemsPerRow = itemsPerRow === 5 ? 6 : itemsPerRow

  const getColSpan = (count: number) => {
    if (count === 0) return 1

    return Math.round(12 / count) // Better fit than floor()
  }

  return (
    <main className="space-y-4 p-4">
      <div className="flex w-full justify-between pt-4">
        <div>
          <Header>Manage Consumers</Header>
        </div>
        <div className="flex items-center gap-3">
          <Label>Set view</Label>
          <SelectTimeRange range={timeRange} setRange={setTimeRange} />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        {(isConsumersStatusOverviewLoading
          ? Array.from({ length: itemsPerRow })
          : items
        ).map((entry: any, index) => {
          const key = isConsumersStatusOverviewLoading ? index : entry[0]
          const consumer = isConsumersStatusOverviewLoading
            ? undefined
            : entry?.[1]

          const currentRow = index < itemsPerRow ? 0 : 1
          const itemsInThisRow =
            currentRow === 0 ? itemsPerRow : itemsCount - itemsPerRow

          const colSpan = getColSpan(itemsInThisRow)

          return (
            <div
              key={key}
              className={`col-span-12 w-full sm:col-span-6 md:col-span-${colSpan}`}
            >
              {renderOverviewCard(
                consumer?.label ?? "Loading...",
                consumer?.icon ?? "loader",
                consumer
                  ? {
                      total: consumer.total,
                      percentage: consumer.percentage,
                      status: consumer.status,
                    }
                  : undefined,
              )}
            </div>
          )
        })}
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-3">
        <Card className="col-span-2">
          <CardHeader className="flex flex-col items-center justify-between py-2 lg:flex-row">
            <CardTitle>Consumer Analytics</CardTitle>
            <div>
              <DateRangePicker
                date={consumerDailyAnalyticsCountDate}
                setDate={setConsumerDailyAnalyticsCountDate}
              />
            </div>
          </CardHeader>
          <CardContent>
            {/* Show loading when refetching */}
            {consumerDailyAnalyticsCountLoading ? (
              <div className="flex h-[240px] items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : consumerDailyAnalyticsCount ? (
              <BarChartStacked
                data={consumerDailyAnalyticsCount}
                config={consumerChartConfig}
              />
            ) : (
              <div className="flex h-[240px] items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-col items-center justify-between py-2 lg:flex-row">
            <CardTitle>Consumer Statistics</CardTitle>
            <DateRangePicker
              date={consumerPieAnalyticsDate}
              setDate={setConsumerPieAnalyticsDate}
            />
          </CardHeader>
          <CardContent>
            {isConsumersAnalyticsPieChartDataLoading ? (
              <div className="flex h-[240px] items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : consumersAnalyticsPieChartData ? (
              <div className="flex h-full items-center justify-center">
                <PieChartComponent
                  data={consumersAnalyticsPieChartData}
                  dataKey="count"
                  nameKey="consumer_status"
                  className="height:1000px"
                  config={consumerChartConfig}
                />
              </div>
            ) : (
              <div className="flex h-[240px] items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <div className="grid grid-cols-1 md:items-stretch">
        <DataTable
          columns={consumerDetailsColumns}
          data={consumerDetails?.results ?? []}
          search={{
            // field: "name",
            label: "Name",
            value: search,
            onChange: setSearch,
          }}
          isLoading={isLoading}
          isError={isError}
          defaultPage={defaultPage}
          setDefaultPage={setDefaultPage}
          nextPageUrl={nextPageUrl}
          prevPageUrl={prevPageUrl}
          page={page}
          setPage={setPage}
          totalCount={consumerDetails?.count}
          zones={zones}
          setZones={setZones}
          books={books}
          setBooks={setBooks}
        >
          <div className="flex w-full justify-end">
            <div className="mx-1">
              <Link
                to="/manage-consumers/add"
                className={buttonVariants({ variant: "default" })}
              >
                <Icons.userPlus /> Add Consumer
              </Link>
            </div>
            <ExportOperationModal
              tableData={consumerDetails?.results ?? [{}]}
              fileName="Manage Consumers"
              showExportModal={showExportModal}
              setShowExportModal={setShowExportModal}
            />
          </div>
        </DataTable>
      </div>
    </main>
  )
}
